import {
    QlBuilderChildNode,
    QlBuilderEntityNode,
    QlBuilderJoinNode,
    QlBuilderModel,
    QlBuilderNode,
} from '../../../builder/ql-builder-model';
import {QL_BUILDER_POLICY_LIMITS, QlBuilderQueryConverter} from '../../../builder/ql-builder-query-converter';
import {FilterCriteria} from '../../../../filter/api/filter.criteria';
import type {QlBuilderNodeContext} from '../interface/ql-builder-node-context.interface';

/**
 * Re-exported from its own file ({@link QlBuilderNodeContext}) rather than redeclared here, so
 * every existing import of `ql-builder-tree-ops` keeps working unchanged.
 */
export type {QlBuilderNodeContext} from '../interface/ql-builder-node-context.interface';

/** A node that can carry fields, filters and children (the root or a regular join). */
export type QlBuilderContainerNode = QlBuilderEntityNode | QlBuilderJoinNode;

/**
 * Pure, immutable operations on a {@link QlBuilderModel} for the builder components. Every method
 * returns NEW objects and never mutates its input — the workbench state only notices a change
 * when it receives a new model object (`UiQlWidgetWorkbenchStateService.setModel`).
 */
export class QlBuilderTreeOps {

    /** Start alias of the root node (api-contract.md "Start-Alias `e`"). */
    static readonly ROOT_ALIAS = 'e';

    /** A fresh model for a newly chosen start entity — always with an explicit field list (cost trap, requirement.md G). */
    static createModel(entityName: string): QlBuilderModel {
        return {
            root: {
                kind: 'entity',
                alias: QlBuilderTreeOps.ROOT_ALIAS,
                entityName,
                fields: [{name: 'id'}],
                filters: [],
                children: [],
            },
            limit: QL_BUILDER_POLICY_LIMITS.defaultPreviewPageSize,
        };
    }

    /** Every node of the tree, depth first, root first. */
    static allNodes(model: QlBuilderModel | null): QlBuilderNode[] {
        const nodes: QlBuilderNode[] = [];
        if (!model?.root) {
            return nodes;
        }
        const walk = (node: QlBuilderNode): void => {
            nodes.push(node);
            if (node.kind === 'entity' || node.kind === 'join') {
                node.children.forEach(walk);
            }
        };
        walk(model.root);
        return nodes;
    }

    static collectAliases(model: QlBuilderModel | null): string[] {
        return QlBuilderTreeOps.allNodes(model).map(node => node.alias);
    }

    /** All child nodes of any kind (regular, extended, side-car). */
    static countJoins(model: QlBuilderModel | null): number {
        return Math.max(0, QlBuilderTreeOps.allNodes(model).length - 1);
    }

    static countExtended(model: QlBuilderModel | null): number {
        return QlBuilderTreeOps.allNodes(model).filter(node => node.kind === 'extended').length;
    }

    static countSideCars(model: QlBuilderModel | null): number {
        return QlBuilderTreeOps.allNodes(model).filter(node => node.kind === 'sideCar').length;
    }

    static hasExtendedOrSideCar(model: QlBuilderModel | null): boolean {
        return QlBuilderTreeOps.allNodes(model).some(node => node.kind === 'extended' || node.kind === 'sideCar');
    }

    /** First free `j<n>` alias. */
    static nextJoinAlias(usedAliases: ReadonlyArray<string>): string {
        let index = 1;
        while (usedAliases.includes(`j${index}`)) {
            index++;
        }
        return `j${index}`;
    }

    static rootContext(model: QlBuilderModel): QlBuilderNodeContext {
        return {
            groupByMode: !!model.groupBy,
            joinCount: QlBuilderTreeOps.countJoins(model),
            extendedCount: QlBuilderTreeOps.countExtended(model),
            sideCarCount: QlBuilderTreeOps.countSideCars(model),
            usedAliases: QlBuilderTreeOps.collectAliases(model),
            depth: 0,
        };
    }

    static childContext(context: QlBuilderNodeContext): QlBuilderNodeContext {
        return {...context, depth: context.depth + 1};
    }

    /**
     * Why no further child node may be attached below a node at `context.depth` — `null` when it
     * may. Covers the two limits every child kind shares (count and depth).
     */
    static addChildBlockedReason(context: QlBuilderNodeContext): string | null {
        if (context.joinCount >= QL_BUILDER_POLICY_LIMITS.maxJoins) {
            return `Maximal ${QL_BUILDER_POLICY_LIMITS.maxJoins} Verknüpfungen je Definition erreicht.`;
        }
        if (context.depth + 1 > QL_BUILDER_POLICY_LIMITS.maxJoinDepth) {
            return `Maximale Verschachtelungstiefe (${QL_BUILDER_POLICY_LIMITS.maxJoinDepth} Ebenen) erreicht.`;
        }
        return null;
    }

    /** Replace (or, with `replacement === null`, remove) the child with `alias` — returns a new node. */
    static replaceChild<T extends QlBuilderContainerNode>(
        node: T,
        alias: string,
        replacement: QlBuilderChildNode | null): T {

        const children: QlBuilderChildNode[] = [];
        for (const child of node.children) {
            if (child.alias !== alias) {
                children.push(child);
            } else if (replacement) {
                children.push(replacement);
            }
        }
        return {...node, children};
    }

    static addChild<T extends QlBuilderContainerNode>(node: T, child: QlBuilderChildNode): T {
        return {...node, children: [...node.children, child]};
    }

    static withRoot(model: QlBuilderModel, root: QlBuilderEntityNode): QlBuilderModel {
        return {...model, root};
    }

    /** Why the grouping mode cannot be switched on — `null` when it can. */
    static enableGroupByBlockedReason(model: QlBuilderModel | null): string | null {
        if (!model) {
            return 'Zuerst eine Start-Entität wählen.';
        }
        if (QlBuilderTreeOps.hasExtendedOrSideCar(model)) {
            return 'Erweiterte Felder und externe Zusatzfelder lassen sich nicht gruppieren – bitte zuerst entfernen.';
        }
        return null;
    }

    /**
     * Switch to "Gruppieren/Aggregieren": the columns are now grouping fields + aggregates, so the
     * root field list and the (column-bound) sortings are cleared; a count aggregate is preset.
     */
    static enableGroupBy(model: QlBuilderModel): QlBuilderModel {
        if (model.groupBy) {
            return model;
        }
        return {
            ...model,
            root: {...model.root, fields: []},
            sortings: [],
            groupBy: {fields: [], aggregates: [{name: '*', as: 'count', fn: 'count'}]},
        };
    }

    /**
     * Result keys (`<alias>.<as|name>`) of every field on a regular JOIN node — in grouping mode these are
     * additional GROUP BY columns (`groupByInconsistent` otherwise), i.e. extra grouping keys that split
     * the groups of the root (QA #5). Row mode: simply the join columns.
     */
    static joinFieldKeys(model: QlBuilderModel | null): string[] {
        const keys: string[] = [];
        const walk = (node: QlBuilderContainerNode): void => {
            for (const child of node.children) {
                if (child.kind === 'join') {
                    child.fields.forEach(field => keys.push(`${child.alias}.${field.as || field.name}`));
                    walk(child);
                }
            }
        };
        if (model?.root) {
            walk(model.root);
        }
        return keys;
    }

    /**
     * "Join-Felder entfernen": every regular join keeps its place (and its filters) but loses its field
     * list, so the grouping keys are the root's again; sortings on those columns are dropped with them.
     */
    static clearJoinFields(model: QlBuilderModel): QlBuilderModel {
        const removed = new Set(QlBuilderTreeOps.joinFieldKeys(model));
        const strip = <T extends QlBuilderContainerNode>(node: T): T => ({
            ...node,
            children: node.children.map(child => child.kind === 'join' ? strip({...child, fields: []}) : child),
        });
        return {
            ...model,
            root: strip(model.root),
            sortings: (model.sortings ?? []).filter(s => !removed.has(s.name)),
        };
    }

    /**
     * Regular joins that carry nothing any more (Q3-8, after "Zusätzliche Schlüssel entfernen"): no fields,
     * no filters, no onlyFirst sortings and no child that carries something (extended/side-car children
     * always count as content). Returns the TOP-MOST such joins only - removing one removes its empty subtree.
     * Such a join still multiplies (1:N, left) or restricts (inner) the rows, so it is offered for removal,
     * never removed silently.
     */
    static emptyJoins(model: QlBuilderModel | null): QlBuilderJoinNode[] {
        const found: QlBuilderJoinNode[] = [];
        const walk = (node: QlBuilderContainerNode): void => {
            for (const child of node.children ?? []) {
                if (child.kind !== 'join') {
                    continue;
                }
                if (QlBuilderTreeOps.isEmptyJoin(child)) {
                    found.push(child);
                } else {
                    walk(child);
                }
            }
        };
        if (model?.root) {
            walk(model.root);
        }
        return found;
    }

    /** Removes every join {@link emptyJoins} reports (with its empty subtree); everything else stays. */
    static removeEmptyJoins(model: QlBuilderModel): QlBuilderModel {
        const strip = <T extends QlBuilderContainerNode>(node: T): T => ({
            ...node,
            children: (node.children ?? [])
                .filter(child => child.kind !== 'join' || !QlBuilderTreeOps.isEmptyJoin(child))
                .map(child => child.kind === 'join' ? strip(child) : child),
        });
        return {...model, root: strip(model.root)};
    }

    private static isEmptyJoin(join: QlBuilderJoinNode): boolean {
        return !join.fields?.length
            && !join.filters?.length
            && !join.sortings?.length
            && (join.children ?? []).every(child => child.kind === 'join' && QlBuilderTreeOps.isEmptyJoin(child));
    }

    /** Back to row mode: grouping removed, the root gets an explicit field list again. */
    static disableGroupBy(model: QlBuilderModel): QlBuilderModel {
        if (!model.groupBy) {
            return model;
        }
        const {groupBy, ...rest} = model;
        return {
            ...rest,
            root: {...model.root, fields: model.root.fields.length ? model.root.fields : [{name: 'id'}]},
            sortings: [],
        };
    }

    /**
     * Result keys a global sorting may name. Row mode: the selected fields of the root and every
     * regular join — except an `onlyFirst` join (spike S5: sorting fails with DISTINCT ON) — plus
     * every side-car column as `<hostAs>.ext_<id>` (spike S6, never the side-car alias). Extended
     * nodes are never sortable. Grouping mode: the grouping columns and aggregates of the root plus
     * the join grouping fields.
     */
    static sortableKeys(model: QlBuilderModel | null): string[] {
        if (!model?.root) {
            return [];
        }
        const keys: string[] = [];
        const key = (alias: string, name: string, as?: string) => `${alias}.${as || name}`;

        if (model.groupBy) {
            model.groupBy.fields.forEach(field => keys.push(key(model.root.alias, field.name, field.as)));
            model.groupBy.aggregates.forEach(aggregate => keys.push(key(model.root.alias, aggregate.name, aggregate.as)));
        } else {
            model.root.fields.forEach(field => keys.push(key(model.root.alias, field.name, field.as)));
        }

        const walk = (node: QlBuilderContainerNode): void => {
            for (const child of node.children) {
                if (child.kind === 'join') {
                    // an onlyFirst join is a separate child unit: neither it nor its subtree is sortable
                    if (!child.onlyFirst) {
                        child.fields.forEach(field => keys.push(key(child.alias, field.name, field.as)));
                        walk(child);
                    }
                } else if (child.kind === 'sideCar' && !model.groupBy) {
                    child.typeFieldIds.forEach(id => keys.push(`${node.alias}.ext_${id}`));
                }
            }
        };
        walk(model.root);

        return Array.from(new Set(keys));
    }

    /**
     * The nodes a query-level condition ("Bedingungen (alle Entitäten)") may filter on: the root and every
     * regular join that is NOT an `onlyFirst` join nor below one (a separate child unit the root's WHERE
     * cannot reach — the backend policy answers `unknownJoin`). Extended and side-car nodes are never
     * filterable (`extendedJoinFilterOrSort` / `sideCarFilter`). Depth first, root first.
     */
    static filterableNodes(model: QlBuilderModel | null): QlBuilderContainerNode[] {
        const nodes: QlBuilderContainerNode[] = [];
        if (!model?.root) {
            return nodes;
        }
        const walk = (node: QlBuilderContainerNode): void => {
            nodes.push(node);
            for (const child of node.children ?? []) {
                if (child.kind === 'join' && !child.onlyFirst) {
                    walk(child);
                }
            }
        };
        walk(model.root);
        return nodes;
    }

    /** Aliases of {@link filterableNodes} — the counterpart of {@link sortableKeys} for conditions. */
    static filterableAliases(model: QlBuilderModel | null): string[] {
        return QlBuilderTreeOps.filterableNodes(model).map(node => node.alias);
    }

    /**
     * The chain of nodes from the root down to the node with `alias` (root first, the node itself last);
     * empty when the alias is not a regular node of the tree.
     */
    static pathTo(model: QlBuilderModel | null, alias: string): QlBuilderContainerNode[] {
        if (!model?.root) {
            return [];
        }
        const find = (node: QlBuilderContainerNode, trail: QlBuilderContainerNode[]): QlBuilderContainerNode[] | null => {
            const current = [...trail, node];
            if (node.alias === alias) {
                return current;
            }
            for (const child of node.children ?? []) {
                if (child.kind === 'join') {
                    const found = find(child, current);
                    if (found) {
                        return found;
                    }
                }
            }
            return null;
        };
        return find(model.root, []) ?? [];
    }

    /** A new model with the query-level conditions replaced (`[]`/`null` removes the member). */
    static withConditions(model: QlBuilderModel, conditions: FilterCriteria[] | null): QlBuilderModel {
        const {conditions: _previous, ...rest} = model;
        return conditions?.length ? {...rest, conditions} : rest;
    }

    /**
     * Keeps the root node honest: a root filter that reaches into another alias (a `j1.x` criterion that
     * arrived via the node dialog or a hand-edited definition) is not a "Start" filter — it is moved to
     * the query-level conditions, so the engine still sees it and the node never shows a foreign field.
     * Returns the same model object when nothing had to move.
     */
    static normalizeRootFilters(model: QlBuilderModel): QlBuilderModel {
        const {rootFilters, conditions} = QlBuilderQueryConverter.splitQueryFilters(model.root.filters, model.root.alias);
        if (!conditions.length && rootFilters.length === (model.root.filters?.length ?? 0)) {
            return model;
        }
        return QlBuilderTreeOps.withConditions(
            {...model, root: {...model.root, filters: rootFilters}},
            [...(model.conditions ?? []), ...conditions]);
    }

    /** A result-column alias safe for QL (`[A-Za-z0-9_]`), unique among `taken`. */
    static uniqueAs(base: string, taken: ReadonlyArray<string>): string {
        const safe = base.replace(/[^A-Za-z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || 'value';
        let candidate = safe;
        let index = 2;
        while (taken.includes(candidate)) {
            candidate = `${safe}${index++}`;
        }
        return candidate;
    }
}
